<template>
  <div class="form-section mb-0">
    <div class="row">

      <!-- Selector de empleado -->
      <div class="col-md-12">
        <div class="form-group">
          <label>Colaborador *</label>
          <SearchableDropdown
            placeholder="Buscar colaborador por nombre o documento"
            :options="employeeList"
            :oldValue="form.employeeId"
            @selected="$emit('employee-selected', $event)"
            :show-error="v.employeeId.$error && !v.employeeId.required"
          />
          <FormError
            v-if="v.employeeId.$error && !v.employeeId.required"
            message="Colaborador es requerido"
          />
        </div>
      </div>

      <!-- Info readonly del empleado seleccionado -->
      <template v-if="employeeInfo.fullName">
        <div class="col-md-6">
          <div class="form-group">
            <label>Tipo de documento</label>
            <FormSelect
              id="empIdentifierType"
              :value="employeeInfo.identifierType"
              :items="identifierTypes"
              defaultOption="—"
              :disabled="true"
            />
          </div>
        </div>

        <div class="col-md-6">
          <div class="form-group">
            <label>Número de documento</label>
            <input
              type="text"
              :value="employeeInfo.identifier"
              disabled
              class="custom-form-control"
            />
          </div>
        </div>

        <div class="col-md-12">
          <div class="form-group">
            <label>Nombre completo</label>
            <input
              type="text"
              :value="employeeInfo.fullName"
              disabled
              class="custom-form-control"
            />
          </div>
        </div>

        <!-- Alerta bloqueante: ya hay contrato vigente en la MISMA empresa -->
        <div class="col-md-12" v-if="hasActiveInSelectedCompany">
          <b-alert show variant="danger" class="d-flex align-items-center">
            <i class="ri-error-warning-line mr-2"></i>
            <span>
              Este empleado ya tiene un contrato vigente en la empresa seleccionada.
              Debe cesarlo primero.
            </span>
          </b-alert>
        </div>

        <!-- Info no bloqueante: tiene contratos vigentes en otras empresas -->
        <div class="col-md-12" v-if="otherActiveCompanies">
          <b-alert show variant="info" class="d-flex align-items-center">
            <i class="ri-information-line mr-2"></i>
            <span>
              Este empleado tiene contratos vigentes en: <strong>{{ otherActiveCompanies }}</strong>.
              Puede crear otro en una empresa distinta.
            </span>
          </b-alert>
        </div>
      </template>

      <!-- Tipo de contrato -->
      <div class="col-md-6">
        <div class="form-group">
          <label>Tipo de contrato *</label>
          <FormSelect
            id="contractType"
            v-model="form.contractType"
            :items="contractDurationList"
            defaultOption="Elegir tipo"
            :showError="v.contractType.$error && !v.contractType.required"
          />
          <FormError
            v-if="v.contractType.$error && !v.contractType.required"
            message="Tipo de contrato es requerido"
          />
        </div>
      </div>

      <!-- Fecha inicio -->
      <div class="col-md-6">
        <div class="form-group">
          <label>Fecha de inicio *</label>
          <FormDatepicker
            label-id="startDate"
            :show-calendar="showStartCalendar"
            @show="showStartCalendar = true"
            @hidden="showStartCalendar = false"
            v-model="form.startDate"
          />
          <FormError
            v-if="v.startDate.$error && !v.startDate.required"
            message="Fecha de inicio es requerida"
          />
        </div>
      </div>

      <!-- Fecha fin (oculta si indeterminado) -->
      <div class="col-md-6" v-if="!isIndefinite">
        <div class="form-group">
          <label>Fecha de fin *</label>
          <FormDatepicker
            label-id="finishDate"
            :show-calendar="showEndCalendar"
            @show="showEndCalendar = true"
            @hidden="showEndCalendar = false"
            v-model="form.finishDate"
          />
          <FormError
            v-if="v.finishDate.$error && !v.finishDate.required"
            message="Fecha de fin es requerida"
          />
        </div>
      </div>

      <!-- Fin período de prueba -->
      <div class="col-md-6">
        <div class="form-group">
          <label>Fin período de prueba</label>
          <FormDatepicker
            label-id="probationEndDate"
            :show-calendar="showProbationCalendar"
            @show="showProbationCalendar = true"
            @hidden="showProbationCalendar = false"
            v-model="form.probationEndDate"
          />
          <FormError
            v-if="probationExceedsLimit"
            message="El período de prueba no puede exceder 6 meses desde la fecha de inicio del contrato"
          />
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import * as moment from 'moment'
import { FormError, FormSelect, SearchableDropdown, FormDatepicker } from 'wize-admin'
import { Constants } from '@/core/utils'

export default {
  components: { FormError, FormSelect, SearchableDropdown, FormDatepicker },
  props: {
    form:                 { type: Object, required: true },
    v:                    { type: Object, required: true },
    contractDurationList: { type: Array,  default: () => [] },
    employeeList:         { type: Array,  default: () => [] },
    employeeInfo:         { type: Object, default: () => ({
      identifierType: null,
      identifier: null,
      fullName: null,
      activeContracts: [],
    }) },
    hasActiveInSelectedCompany: { type: Boolean, default: false },
    otherActiveCompanies:       { type: String,  default: '' },
    isIndefinite:               { type: Boolean, default: false },
  },
  emits: ['employee-selected', 'probation-error'],
  data() {
    return {
      showStartCalendar:    false,
      showEndCalendar:      false,
      showProbationCalendar: false,
      identifierTypes: [
        Constants.IdentifierType.DNI,
        Constants.IdentifierType.CE,
        Constants.IdentifierType.PASAPORTE,
        Constants.IdentifierType.LIBRETA_MILITAR,
        Constants.IdentifierType.CARNET_MILITAR,
      ],
    }
  },
  computed: {
    probationExceedsLimit() {
      if (!this.form.startDate || !this.form.probationEndDate) return false
      const maxDate = moment(this.form.startDate).add(6, 'months')
      return moment(this.form.probationEndDate).isAfter(maxDate)
    },
  },
  watch: {
    probationExceedsLimit(val) {
      this.$emit('probation-error', val)
    },
  },
}
</script>

<style lang="stylus" scoped></style>
